import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, FileText } from 'lucide-react';
import { SITE_CONFIG } from '../config/siteConfig';

type LegalType = 'privacy' | 'terms';

interface LegalModalProps {
    isOpen: boolean;
    type: LegalType | null;
    onClose: () => void;
}

const LegalModal: React.FC<LegalModalProps> = ({ isOpen, type, onClose }) => {
    // Architect: Prevent background scroll when modal is open
    React.useEffect(() => {
        if (isOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isOpen]);

    if (!isOpen || !type) return null;

    const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
    const Icon = type === 'privacy' ? ShieldCheck : FileText;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                />

                {/* Modal Content */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-3xl bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-zinc-800 bg-zinc-900/50">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-zinc-800 rounded-md">
                                <Icon className="w-6 h-6 text-white" />
                            </div>
                            <h2 className="text-xl font-bold text-white uppercase tracking-wide font-display">
                                {title}
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="p-6 md:p-8 overflow-y-auto text-zinc-300 space-y-6 leading-relaxed text-sm md:text-base custom-scrollbar">
                        {type === 'privacy' ? (
                            <>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">1. Recolección de Información</h3>
                                    <p>
                                        En {SITE_CONFIG.brandName}, recolectamos información esencial para procesar tus pedidos y mejorar tu experiencia de compra.
                                        Esto incluye datos proporcionados directamente por ti (nombre, dirección, teléfono) y datos técnicos de navegación (cookies, dirección IP).
                                    </p>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">2. Uso de la Información</h3>
                                    <p>Utilizamos tus datos exclusivamente para:</p>
                                    <ul className="list-disc pl-5 mt-2 space-y-1 text-zinc-400">
                                        <li>Procesar y entregar tus pedidos de manera eficiente.</li>
                                        <li>Enviarte actualizaciones sobre el estado de tu compra.</li>
                                        <li>Mejorar nuestro catálogo y funcionalidad del sitio web.</li>
                                        <li>Cumplir con obligaciones legales y fiscales en El Salvador.</li>
                                    </ul>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">3. Derechos ARCO</h3>
                                    <p>
                                        Conforme a la legislación salvadoreña, tienes derecho a Acceder, Rectificar, Cancelar u Oponerte al tratamiento de tus datos personales.
                                        Para ejercer estos derechos, por favor contáctanos a través de nuestros canales oficiales de WhatsApp.
                                    </p>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">4. Seguridad de Datos</h3>
                                    <p>
                                        Implementamos medidas de seguridad técnicas y organizativas para proteger tus datos contra acceso no autorizado, alteración o pérdida.
                                        No compartimos tu información con terceros con fines comerciales sin tu consentimiento explícito.
                                    </p>
                                </section>
                            </>
                        ) : (
                            <>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">1. Aceptación de Términos</h3>
                                    <p>
                                        Al acceder y realizar compras en {SITE_CONFIG.brandName}, aceptas estos términos y condiciones en su totalidad.
                                        Nos reservamos el derecho de modificar estos términos en cualquier momento.
                                    </p>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">2. Productos y Garantía</h3>
                                    <p>
                                        Nos esforzamos por mostrar los colores y detalles de nuestros productos con la mayor precisión posible.
                                        Todos nuestros productos cuentan con una <strong>garantía de 12 meses</strong> por defectos de fábrica, tal como se indica en nuestra política de garantía.
                                        La garantía no cubre daños por mal uso, golpes, humedad o desgaste natural.
                                    </p>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">3. Envíos y Entregas</h3>
                                    <p>
                                        Realizamos envíos a los 14 departamentos de El Salvador.
                                        Los tiempos de entrega pueden variar según la zona y la disponibilidad de la empresa de logística.
                                        Nos comprometemos a despachar tu pedido en el menor tiempo posible una vez confirmada la orden.
                                    </p>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">4. Política de Devoluciones</h3>
                                    <p>
                                        Aceptamos cambios únicamente por defectos de fábrica dentro del periodo de garantía.
                                        No se realizan devoluciones de dinero por insatisfacción o error en la selección del modelo por parte del cliente,
                                        sin embargo, podemos evaluar cambios por otros productos de igual valor sujeto a disponibilidad y estado del empaque original.
                                    </p>
                                </section>
                                <section>
                                    <h3 className="text-white font-bold text-lg mb-3">5. Propiedad Intelectual</h3>
                                    <p>
                                        Todo el contenido de este sitio (imágenes, logotipos, textos) es propiedad de {SITE_CONFIG.brandName} o sus proveedores
                                        y está protegido por las leyes de propiedad intelectual.
                                    </p>
                                </section>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-zinc-800 bg-zinc-900/50 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-white text-black font-bold uppercase text-sm tracking-wider hover:bg-zinc-200 transition-colors"
                        >
                            Entendido
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default LegalModal;
